/*
  AuthContext — single source of truth for the signed-in user.
  On mount, calls /auth/me. Exposes login/register/logout helpers plus a
  `loading` flag for the initial bootstrap.
*/
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { auth } from "../lib/authApi";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me();
      setUser(me);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = async (email, password) => {
    const u = await auth.login(email, password);
    setUser(u);
    return u;
  };

  const register = async (email, password, name) => {
    const u = await auth.register(email, password, name);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await auth.logout(); } catch { /* noop */ }
    setUser(null);
  };

  return (
    <Ctx.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
}
